import { Badge, Box, Checkbox, FormControl, FormLabel, HStack, Text } from "@chakra-ui/react";
import React from "react";
import SendEmailButton from "../../../../../../shared/components/SendEmailButton";
import SendSMSButton from "../../../../../../shared/components/SendSMSButton";
import type { PatientId } from "../../../../../../shared/schema/schema";
import { phoneFormatter } from "../../../../../../shared/utils/phone-formatter";
import AddressAutocompleteInput from "../../../../../office-branch/components/AddressAutocompleteInput";
import {
  EntityProfileContactCardShell,
  EntityProfileContactGrid,
  EntityProfileContactInputField,
  EntityProfileContactPhoneField,
  EntityProfileContactSelectField,
} from "../../../../../entityProfileModal/components/EntityProfileContactCard";
import { entityProfileModalColors } from "../../../../../entityProfileModal/entityProfileModal.theme";
import type { PatientProfileBridgeActions } from "../../../../patientProfileModal.actions";
import { formatPatientProfileValue } from "../../../../patientProfileModal.utils";
import {
  type EditPatientContact,
  type PatientContact,
  relationshipOptions,
} from "../patientContacts.utils";
import EntityModalButton from "../../../../../entityProfileModal/components/EntityModalButton";

function getContactFullName(contact: PatientContact) {
  return [contact.firstName, contact.lastName].filter(Boolean).join(" ") || "Patient contact";
}

function normalizeComparablePhoneNumber(phoneNumber: string | null | undefined) {
  if (phoneNumber === null || phoneNumber === undefined || phoneNumber.trim() === "") {
    return null;
  }

  return phoneFormatter.isValidNumber(phoneNumber)
    ? phoneFormatter.formatE164(phoneNumber)
    : phoneNumber;
}

function isMainPhoneNumber(phoneNumber: string | null | undefined, mainPhoneNumber: string | null) {
  const normalizedPhoneNumber = normalizeComparablePhoneNumber(phoneNumber);
  const normalizedMainPhoneNumber = normalizeComparablePhoneNumber(mainPhoneNumber);

  return (
    normalizedPhoneNumber !== null &&
    normalizedMainPhoneNumber !== null &&
    normalizedPhoneNumber === normalizedMainPhoneNumber
  );
}

function getMainPhoneNumberValue(phoneNumber: string | null | undefined) {
  if (phoneNumber === null || phoneNumber === undefined || phoneNumber.trim() === "") {
    return null;
  }

  return phoneFormatter.isValidNumber(phoneNumber) ? phoneFormatter.formatE164(phoneNumber) : null;
}

function ContactBadge(props: { children: React.ReactNode }) {
  return (
    <Badge colorScheme="blue" fontSize="10px" lineHeight="1.2">
      {props.children}
    </Badge>
  );
}

function CallPhoneValue(props: {
  actions: PatientProfileBridgeActions;
  isOnboardingAgency: boolean;
  isSmsable?: boolean;
  label: string;
  mainPhoneNumber: string | null;
  patientId: PatientId;
  phoneNumber: string | null | undefined;
}) {
  if (props.phoneNumber === null || props.phoneNumber === undefined || props.phoneNumber === "") {
    return <>{formatPatientProfileValue(props.phoneNumber)}</>;
  }

  return (
    <HStack flexWrap="wrap" spacing={1}>
      <span>{formatPatientProfileValue(props.phoneNumber)}</span>
      {props.isOnboardingAgency ? (
        <>
          <EntityModalButton
            isIconOnly
            aria-label={`Call ${props.label}`}
            iconClassName="fa fa-phone"
            kind="white"
            size="xs"
            title="Call"
            onClick={() => props.actions.openDialer({ predefinedPhoneNumber: props.phoneNumber! })}
          />
          {props.isSmsable === true ? (
            <SendSMSButton
              buttonProps={{ h: "24px", minH: "24px", px: 2, size: "xs" }}
              entity="Patient"
              patientId={props.patientId}
              predefinedPhoneNumber={props.phoneNumber}
              text="SMS"
            />
          ) : null}
          {isMainPhoneNumber(props.phoneNumber, props.mainPhoneNumber) ? (
            <ContactBadge>Main</ContactBadge>
          ) : null}
        </>
      ) : null}
    </HStack>
  );
}

function MainPhoneCheckbox(props: {
  isDisabled: boolean;
  label: string;
  mainPhoneNumber: string | null;
  phoneNumber: string | null | undefined;
  onUpdateMainPhoneNumber: (phoneNumber: string | null) => void;
}) {
  const mainPhoneNumberValue = getMainPhoneNumberValue(props.phoneNumber);

  return (
    <Checkbox
      isChecked={isMainPhoneNumber(props.phoneNumber, props.mainPhoneNumber)}
      isDisabled={props.isDisabled || mainPhoneNumberValue === null}
      size="sm"
      onChange={() => props.onUpdateMainPhoneNumber(mainPhoneNumberValue)}
    >
      {props.label}
    </Checkbox>
  );
}

function ReadOnlyPatientContactField(props: { label: string; value: React.ReactNode }) {
  return (
    <Box>
      <Text
        color={entityProfileModalColors.labelText}
        fontSize="12px"
        fontWeight={700}
        letterSpacing="0.02em"
        textTransform="uppercase"
      >
        {props.label}
      </Text>
      <Box color={entityProfileModalColors.text} fontSize="14px" lineHeight="1.35" minH="19px">
        {props.value}
      </Box>
    </Box>
  );
}

function PatientContactAddressAutocompleteField(props: {
  addressTimezone: string;
  contact: EditPatientContact;
  isDisabled: boolean;
  onChange: (contact: EditPatientContact) => void;
}) {
  return (
    <FormControl>
      <FormLabel fontSize="13px" fontWeight={700}>
        Address
      </FormLabel>
      <AddressAutocompleteInput
        addressText={props.contact.address ?? ""}
        isDisabled={props.isDisabled}
        value={props.contact.addressComponents?.components ?? null}
        onChange={(address, addressComponents) => {
          props.onChange({
            ...props.contact,
            address,
            addressComponents: {
              text: address,
              timezone: props.contact.addressComponents?.timezone ?? props.addressTimezone,
              components: addressComponents,
            },
          });
        }}
        onClear={() =>
          props.onChange({
            ...props.contact,
            address: null,
            addressComponents: null,
          })
        }
      />
    </FormControl>
  );
}

export function ReadOnlyPatientContactCard(props: {
  actions: PatientProfileBridgeActions;
  contact: PatientContact;
  index: number;
  isOnboardingAgency: boolean;
  mainPhoneNumber: string | null;
  patientId: PatientId;
}) {
  const { contact } = props;

  return (
    <EntityProfileContactCardShell title={`Contact #${props.index + 1}`}>
      <EntityProfileContactGrid columns={3}>
        <ReadOnlyPatientContactField
          label="First Name"
          value={formatPatientProfileValue(contact.firstName)}
        />
        <ReadOnlyPatientContactField
          label="Middle Name"
          value={formatPatientProfileValue(contact.middleName)}
        />
        <ReadOnlyPatientContactField
          label="Last Name"
          value={formatPatientProfileValue(contact.lastName)}
        />
        <ReadOnlyPatientContactField
          label="Relationship"
          value={formatPatientProfileValue(contact.relationship)}
        />
        <ReadOnlyPatientContactField
          label="Mobile Phone"
          value={
            <CallPhoneValue
              actions={props.actions}
              isOnboardingAgency={props.isOnboardingAgency}
              isSmsable={contact.mobilePhoneNumberSmsable}
              label="mobile phone"
              mainPhoneNumber={props.mainPhoneNumber}
              patientId={props.patientId}
              phoneNumber={contact.mobilePhoneNumber}
            />
          }
        />
        {props.isOnboardingAgency && contact.isForPortalLogin ? (
          <ReadOnlyPatientContactField
            label="Portal Login"
            value={<ContactBadge>Used for Login</ContactBadge>}
          />
        ) : null}
        <ReadOnlyPatientContactField
          label="Home Phone"
          value={
            <CallPhoneValue
              actions={props.actions}
              isOnboardingAgency={props.isOnboardingAgency}
              label="home phone"
              mainPhoneNumber={props.mainPhoneNumber}
              patientId={props.patientId}
              phoneNumber={contact.homePhoneNumber}
            />
          }
        />
        <ReadOnlyPatientContactField
          label="Address"
          value={formatPatientProfileValue(contact.address)}
        />
        <ReadOnlyPatientContactField
          label="Address line 2"
          value={formatPatientProfileValue(contact.address2)}
        />
        <ReadOnlyPatientContactField
          label="Email"
          value={
            props.isOnboardingAgency && contact.email ? (
              <HStack flexWrap="wrap" spacing={1}>
                <span>{contact.email}</span>
                <SendEmailButton
                  buttonProps={{ h: "24px", minH: "24px", px: 2, size: "xs" }}
                  defaultAssignedTeam="Staffing"
                  emails={[{ email: contact.email, name: getContactFullName(contact) }]}
                  newTicketDefaultValues={{
                    topic: "Patient",
                    caregiverId: null,
                    patientId: props.patientId,
                  }}
                  text="Send email"
                />
              </HStack>
            ) : (
              formatPatientProfileValue(contact.email)
            )
          }
        />
      </EntityProfileContactGrid>
    </EntityProfileContactCardShell>
  );
}

export function PatientContactEditorCard(props: {
  addressTimezone: string;
  contact: EditPatientContact;
  index: number;
  isDisabled: boolean;
  isOnboardingAgency: boolean;
  mainPhoneNumber: string | null;
  showErrors: boolean;
  onChange: (index: number, contact: EditPatientContact) => void;
  onRemove: (index: number) => void;
  onUpdateMainPhoneNumber: (phoneNumber: string | null) => void;
}) {
  const updateField = <TKey extends keyof EditPatientContact>(
    key: TKey,
    value: EditPatientContact[TKey]
  ) => {
    props.onChange(props.index, { ...props.contact, [key]: value });
  };

  return (
    <EntityProfileContactCardShell
      isRemoveDisabled={props.isDisabled}
      removeLabel="Remove contact"
      title={`Contact #${props.index + 1}`}
      variant="editing"
      onRemove={() => props.onRemove(props.index)}
    >
      <EntityProfileContactGrid columns={3}>
        <EntityProfileContactInputField
          errorMessage="First name is required."
          isDisabled={props.isDisabled}
          isInvalid={props.showErrors && props.contact.firstName.trim() === ""}
          isRequired={true}
          label="First Name"
          value={props.contact.firstName}
          onChange={(value) => updateField("firstName", value)}
        />
        <EntityProfileContactInputField
          isDisabled={props.isDisabled}
          label="Middle Name"
          value={props.contact.middleName ?? ""}
          onChange={(value) => updateField("middleName", value || null)}
        />
        <EntityProfileContactInputField
          errorMessage="Last name is required."
          isDisabled={props.isDisabled}
          isInvalid={props.showErrors && props.contact.lastName.trim() === ""}
          isRequired={true}
          label="Last Name"
          value={props.contact.lastName}
          onChange={(value) => updateField("lastName", value)}
        />
        <EntityProfileContactSelectField
          errorMessage="Relationship is required."
          isDisabled={props.isDisabled}
          isInvalid={props.showErrors && props.contact.relationship.trim() === ""}
          isRequired={true}
          label="Relationship"
          options={relationshipOptions}
          placeholder="Select relationship"
          value={props.contact.relationship}
          onChange={(value) => updateField("relationship", value)}
        />
        <EntityProfileContactPhoneField
          errorMessage="Enter a valid mobile phone number."
          isDisabled={props.isDisabled}
          label="Mobile phone number"
          showErrors={props.showErrors}
          value={props.contact.mobilePhoneNumber ?? ""}
          onChange={(value) => updateField("mobilePhoneNumber", value)}
        />
        {props.isOnboardingAgency ? (
          <Box alignSelf="center">
            <MainPhoneCheckbox
              isDisabled={props.isDisabled}
              label="Main mobile phone"
              mainPhoneNumber={props.mainPhoneNumber}
              phoneNumber={props.contact.mobilePhoneNumber}
              onUpdateMainPhoneNumber={props.onUpdateMainPhoneNumber}
            />
          </Box>
        ) : null}
        <EntityProfileContactPhoneField
          errorMessage="Enter a valid home phone number."
          isDisabled={props.isDisabled}
          label="Home phone number"
          showErrors={props.showErrors}
          value={props.contact.homePhoneNumber ?? ""}
          onChange={(value) => updateField("homePhoneNumber", value || null)}
        />
        {props.isOnboardingAgency ? (
          <Box alignSelf="center">
            <MainPhoneCheckbox
              isDisabled={props.isDisabled}
              label="Main home phone"
              mainPhoneNumber={props.mainPhoneNumber}
              phoneNumber={props.contact.homePhoneNumber}
              onUpdateMainPhoneNumber={props.onUpdateMainPhoneNumber}
            />
          </Box>
        ) : null}
        <PatientContactAddressAutocompleteField
          addressTimezone={props.addressTimezone}
          contact={props.contact}
          isDisabled={props.isDisabled}
          onChange={(contact) => props.onChange(props.index, contact)}
        />
        <EntityProfileContactInputField
          isDisabled={props.isDisabled}
          label="Address line 2"
          value={props.contact.address2 ?? ""}
          onChange={(value) => updateField("address2", value || null)}
        />
        <EntityProfileContactInputField
          isDisabled={props.isDisabled}
          label="Email"
          value={props.contact.email ?? ""}
          onChange={(value) => updateField("email", value || null)}
        />
      </EntityProfileContactGrid>
    </EntityProfileContactCardShell>
  );
}
